import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Drawer, Typography } from '@mui/material';
import { ArrowIcon, EditPenIcon, ImageIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import NewContactDrawer from 'app/pages/crm/shared/components/NewContactDrawer';
import NewLeadModal from 'app/pages/loanApplication/tabs/lead/NewLeadModal';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import { openDialogAction, showMessageAction } from 'store/global/actions';

import DocumentCaptureSession from './components/DocumentCaptureSession';
import MethodCard from './components/MethodCard';
import ObjectOptionRow from './components/ObjectOptionRow';
import {
  CONTINUE_WITH_PHOTOS_LABEL,
  CREATE_OBJECT,
  CREATE_OBJECT_LABEL,
  CREATE_OBJECT_OPTIONS,
  CREATE_STEP,
  ENTER_DETAILS_COPY,
  ENTER_DETAILS_ICON_COLOR,
  FORK_PROMPT,
  PHOTOS_DETAILS_BY_OBJECT,
  PHOTOS_ICON_COLOR,
  START_WITH_PHOTOS_COPY,
} from './CreateSheet.constants';
import {
  backButtonStyles,
  continueWithPhotosStyles,
  drawerPaperStyles,
  forkHeaderStyles,
  forkPromptStyles,
  grabberStyles,
  methodListStyles,
  pickerListStyles,
  sheetHeaderStyles,
} from './CreateSheet.styles';
import { CreateSheetTestIds } from './CreateSheet.testIds';

const drawerPaperProps = { sx: drawerPaperStyles };

/**
 * The mobile "+" create flow as a bottom sheet:
 *  - PICKER: choose the object to create (Contact / Lead / Loan), filtered to what the
 *    user is permitted to create.
 *  - FORK: choose how to start — "Enter details" routes to the existing manual create
 *    surface for that object; "Start with photos" opens the multi-document capture
 *    session (DEV-4252). Upload + server-side extraction reuse the DEV-4250 path; the
 *    draft-create that supplies the upload target is downstream (DEV-4251).
 *
 * The three create surfaces are re-hosted here so they are reachable globally
 * (Loan drawer + Contact drawer own local open-state; Lead uses the global dialog).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {boolean} [props.canCreateLoan]
 * @param {boolean} [props.canCreateLead]
 * @param {boolean} [props.canCreateContact]
 */
const CreateSheet = ({
  isOpen,
  onClose,
  canCreateLoan = false,
  canCreateLead = false,
  canCreateContact = false,
}) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState(CREATE_STEP.PICKER);
  const [selectedObject, setSelectedObject] = useState(null);
  const [isContactDrawerOpen, setIsContactDrawerOpen] = useState(false);
  const [captureObject, setCaptureObject] = useState(null);

  const availableByObject = useMemo(
    () => ({
      [CREATE_OBJECT.CONTACT]: canCreateContact,
      [CREATE_OBJECT.LEAD]: canCreateLead,
      [CREATE_OBJECT.LOAN]: canCreateLoan,
    }),
    [canCreateContact, canCreateLead, canCreateLoan],
  );

  const availableOptions = useMemo(
    () => CREATE_OBJECT_OPTIONS.filter(option => availableByObject[option.id]),
    [availableByObject],
  );

  const resetAndClose = useCallback(() => {
    setStep(CREATE_STEP.PICKER);
    setSelectedObject(null);
    onClose();
  }, [onClose]);

  const onSelectObject = useCallback(objectId => {
    setSelectedObject(objectId);
    setStep(CREATE_STEP.FORK);
  }, []);

  const onBackToPicker = useCallback(() => {
    setStep(CREATE_STEP.PICKER);
    setSelectedObject(null);
  }, []);

  const onCloseContactDrawer = useCallback(() => {
    setIsContactDrawerOpen(false);
  }, []);

  const onStartWithPhotos = useCallback(() => {
    setCaptureObject(selectedObject);
    resetAndClose();
  }, [selectedObject, resetAndClose]);

  const onCloseCapture = useCallback(() => {
    setCaptureObject(null);
  }, []);

  // Integration seam: the captured batch is handed off here. The draft-create
  // that turns a batch into a real object + upload target (applicationId) is
  // downstream (DEV-4251) and not built in this pass — so for now we just
  // acknowledge the batch. When draft-create lands, this callback creates the
  // draft and passes its applicationId to DocumentCaptureSession to upload.
  const onCaptureSubmit = useCallback(
    captured => {
      dispatch(
        showMessageAction({
          message: `${captured.length} document${
            captured.length === 1 ? '' : 's'
          } ready to start your ${CREATE_OBJECT_LABEL[captureObject] ?? 'record'}.`,
        }),
      );
    },
    [dispatch, captureObject],
  );

  const onEnterDetails = useCallback(() => {
    if (selectedObject === CREATE_OBJECT.LOAN) {
      resetAndClose();
      dispatch(
        openDialogAction({
          header: 'New Loan',
          isRestyled: true,
          size: ModalSize.XL,
          withDivider: true,
          body: dialogProps => <NewLeadModal createAsLoan {...dialogProps} />,
        }),
      );

      return;
    }

    if (selectedObject === CREATE_OBJECT.CONTACT) {
      resetAndClose();
      setIsContactDrawerOpen(true);

      return;
    }

    if (selectedObject === CREATE_OBJECT.LEAD) {
      resetAndClose();
      dispatch(
        openDialogAction({
          header: 'New Lead',
          isRestyled: true,
          size: ModalSize.XL,
          withDivider: true,
          body: dialogProps => <NewLeadModal {...dialogProps} />,
        }),
      );
    }
  }, [selectedObject, resetAndClose, dispatch]);

  const isPicker = step === CREATE_STEP.PICKER;
  const photosDetails = selectedObject ? PHOTOS_DETAILS_BY_OBJECT[selectedObject] : null;

  return (
    <>
      <Drawer
        PaperProps={drawerPaperProps}
        anchor="bottom"
        data-testid={CreateSheetTestIds.DRAWER}
        open={isOpen}
        onClose={resetAndClose}
      >
        <Box sx={grabberStyles} />
        {isPicker ? (
          <>
            <Box sx={sheetHeaderStyles}>
              <Typography variant="medium20">Create new…</Typography>
            </Box>
            <Box sx={pickerListStyles}>
              {availableOptions.map(option => (
                <ObjectOptionRow key={option.id} option={option} onSelect={onSelectObject} />
              ))}
            </Box>
          </>
        ) : (
          <>
            <Box sx={forkHeaderStyles}>
              <Box
                aria-label="Back"
                component="button"
                data-testid={CreateSheetTestIds.BACK}
                sx={backButtonStyles}
                onClick={onBackToPicker}
              >
                <ArrowIcon direction={IconDirection.LEFT} size={IconSize[20]} />
              </Box>
              <Typography variant="medium20">
                {`New ${CREATE_OBJECT_LABEL[selectedObject]}`}
              </Typography>
            </Box>
            <Typography color="text.secondary" sx={forkPromptStyles} variant="regular12">
              {FORK_PROMPT}
            </Typography>
            <Box sx={methodListStyles}>
              <MethodCard
                icon={EditPenIcon}
                iconColor={ENTER_DETAILS_ICON_COLOR}
                subtitle={ENTER_DETAILS_COPY.subtitle}
                testId="create-sheet-method-enter-details"
                title={ENTER_DETAILS_COPY.title}
                onPress={onEnterDetails}
              />
              <MethodCard
                isHighlighted
                chips={photosDetails?.chips}
                icon={ImageIcon}
                iconColor={PHOTOS_ICON_COLOR}
                subtitle={photosDetails?.subtitle}
                testId="create-sheet-method-photos"
                title={START_WITH_PHOTOS_COPY.title}
                onPress={onStartWithPhotos}
              />
              <Button
                data-testid={CreateSheetTestIds.CONTINUE_PHOTOS}
                sx={continueWithPhotosStyles}
                variant="contained"
                onClick={onStartWithPhotos}
              >
                <Typography variant="medium16">{CONTINUE_WITH_PHOTOS_LABEL}</Typography>
              </Button>
            </Box>
          </>
        )}
      </Drawer>

      <NewContactDrawer open={isContactDrawerOpen} onClose={onCloseContactDrawer} />

      <DocumentCaptureSession
        isOpen={!!captureObject}
        objectLabel={captureObject ? CREATE_OBJECT_LABEL[captureObject] : ''}
        onClose={onCloseCapture}
        onSubmit={onCaptureSubmit}
      />
    </>
  );
};

CreateSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  canCreateLoan: PropTypes.bool,
  canCreateLead: PropTypes.bool,
  canCreateContact: PropTypes.bool,
};

export default memo(CreateSheet);
